import React, { useState, useMemo, useRef, useEffect } from "react";
import { MapContainer, TileLayer, Polygon, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { X, Save, Undo2, Trash2, MousePointerClick, MapPin, CheckCircle2, Edit3, Plus } from "lucide-react";
import { dataApi, formatApiError } from "../api/client";

// Centro por defecto: Puerto Maldonado
const DEFAULT_CENTER = [-12.5933, -69.1893];
const DEFAULT_ZOOM = 14;

// ─── Iconos de vértices ────────────────────────────────────────
const _vertexIcon = (index, isFirst) => L.divIcon({
  className: "",
  html: `
    <div style="
      width:22px;height:22px;border-radius:50%;
      background:${isFirst ? "#00A651" : "#3949AB"};
      border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.4);
      display:flex;align-items:center;justify-content:center;
      color:white;font-size:10px;font-weight:900;font-family:monospace;
      cursor:grab;
    ">${index + 1}</div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const _midpointIcon = L.divIcon({
  className: "",
  html: `
    <div style="
      width:14px;height:14px;border-radius:50%;
      background:white;border:2px dashed #3949AB;
      box-shadow:0 1px 3px rgba(0,0,0,.3);
      cursor:cell;
      transition:transform .15s;
    " onmouseover="this.style.transform='scale(1.4)';this.style.background='#3949AB';"
      onmouseout="this.style.transform='scale(1)';this.style.background='white';"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

// Handler para capturar clicks del mapa y agregar vértices (append al final)
const PolygonClickHandler = ({ onAddPoint, enabled }) => {
  useMapEvents({
    click(e) {
      if (enabled) onAddPoint([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
};

// Auto-fit del mapa cuando ya hay polígono guardado
const FitToPolygon = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points && points.length >= 3) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
};

export default function ZonePolygonEditor({ zone, onClose, onSaved }) {
  const initial = Array.isArray(zone.polygon) ? zone.polygon : [];
  const [points, setPoints] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);
  // Modo: "add" = clic en mapa agrega vértices al final; "edit" = solo editar existentes (drag/insert/delete)
  const [mode, setMode] = useState(initial.length >= 3 ? "edit" : "add");
  const historyRef = useRef([initial]);

  const center = useMemo(() => {
    if (zone.lat && zone.lng) return [zone.lat, zone.lng];
    if (initial.length >= 1) return initial[0];
    return DEFAULT_CENTER;
  }, [zone.lat, zone.lng, initial]);

  const pushHistory = (next) => {
    historyRef.current = [...historyRef.current.slice(-30), next];
    setPoints(next);
  };

  const handleAddPoint = (pt) => pushHistory([...points, pt]);

  // Arrastrar un vértice → actualiza su posición.
  const handleDragVertex = (idx, newLatLng) => {
    const next = points.map((p, i) => i === idx ? [newLatLng.lat, newLatLng.lng] : p);
    pushHistory(next);
  };

  // Doble click en vértice → elimina (mínimo 3 vértices).
  const handleDeleteVertex = (idx) => {
    if (points.length <= 3) {
      setErr("Un polígono necesita al menos 3 vértices.");
      setTimeout(() => setErr(""), 2500);
      return;
    }
    const next = points.filter((_, i) => i !== idx);
    pushHistory(next);
  };

  // Click en punto medio → inserta nuevo vértice entre 2 existentes.
  const handleInsertMidpoint = (edgeIdx) => {
    const a = points[edgeIdx];
    const b = points[(edgeIdx + 1) % points.length];
    const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const next = [...points.slice(0, edgeIdx + 1), mid, ...points.slice(edgeIdx + 1)];
    pushHistory(next);
  };

  const undo = () => {
    if (historyRef.current.length <= 1) {
      pushHistory([]);
      return;
    }
    historyRef.current = historyRef.current.slice(0, -1);
    setPoints(historyRef.current[historyRef.current.length - 1] || []);
  };

  const clearAll = () => {
    if (!points.length) return;
    if (!window.confirm("¿Borrar todos los vértices del polígono?")) return;
    pushHistory([]);
    setMode("add");
  };

  const save = async () => {
    setErr("");
    if (points.length < 3) {
      setErr("Un polígono necesita al menos 3 vértices.");
      return;
    }
    setSaving(true);
    try {
      await dataApi.updateZonePolygon(zone.id, points);
      setSaved(true);
      setTimeout(() => {
        if (onSaved) onSaved();
      }, 500);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Midpoints — solo si hay polígono cerrado (>=3 puntos) Y estamos en modo edición.
  const midpoints = useMemo(() => {
    if (mode !== "edit" || points.length < 3) return [];
    return points.map((p, i) => {
      const next = points[(i + 1) % points.length];
      return { edgeIdx: i, pos: [(p[0] + next[0]) / 2, (p[1] + next[1]) / 2] };
    });
  }, [points, mode]);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose}></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden"
          data-testid="zone-polygon-editor-modal"
        >
          {/* Header */}
          <div className="border-b px-5 py-3 flex items-center justify-between shrink-0">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-[#3949AB]">
                Editor de polígono
              </div>
              <h3 className="font-display font-semibold text-lg leading-tight">
                {zone.name}{" "}
                <span className="font-mono text-xs text-neutral-500">({zone.code})</span>
              </h3>
            </div>
            <button
              onClick={onClose}
              className="btn-ghost !p-1.5"
              data-testid="zone-polygon-close"
            >
              <X className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>

          {/* Instrucciones + toolbar */}
          <div className="bg-[#F8F9FC] border-b px-5 py-3 flex items-center gap-3 flex-wrap shrink-0">
            <div className="flex items-center gap-1 bg-white border border-neutral-300 rounded-md p-0.5">
              <button
                type="button"
                onClick={() => setMode("add")}
                className={`px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 transition ${
                  mode === "add" ? "bg-[#3949AB] text-white" : "text-neutral-600 hover:bg-neutral-100"
                }`}
                data-testid="zone-polygon-mode-add"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Agregar vértices
              </button>
              <button
                type="button"
                onClick={() => setMode("edit")}
                disabled={points.length < 3}
                className={`px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 transition disabled:opacity-40 ${
                  mode === "edit" ? "bg-[#3949AB] text-white" : "text-neutral-600 hover:bg-neutral-100"
                }`}
                data-testid="zone-polygon-mode-edit"
              >
                <Edit3 className="w-3.5 h-3.5" strokeWidth={2.5} /> Editar existentes
              </button>
            </div>
            <div className="flex items-center gap-2 text-xs text-neutral-600">
              <MousePointerClick className="w-4 h-4 text-[#3949AB]" strokeWidth={2.5} />
              {mode === "add"
                ? "Haz clic en el mapa para agregar vértices en orden"
                : "Arrastra un vértice · Doble-clic para eliminarlo · Clic en el punto medio para insertar"}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="chip chip-info" data-testid="zone-polygon-count">
                {points.length} vértices
              </span>
              <button
                type="button"
                onClick={undo}
                disabled={points.length === 0 || saving}
                className="btn-secondary text-xs"
                data-testid="zone-polygon-undo"
              >
                <Undo2 className="w-3.5 h-3.5" strokeWidth={2.5} /> Deshacer
              </button>
              <button
                type="button"
                onClick={clearAll}
                disabled={points.length === 0 || saving}
                className="btn-secondary text-xs !text-[#D13438]"
                data-testid="zone-polygon-clear"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} /> Limpiar
              </button>
            </div>
          </div>

          {/* Mapa */}
          <div className="relative flex-1 min-h-0">
            <MapContainer
              center={center}
              zoom={DEFAULT_ZOOM}
              scrollWheelZoom
              className="h-full w-full"
              doubleClickZoom={false}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap'
              />

              <PolygonClickHandler onAddPoint={handleAddPoint} enabled={mode === "add"} />
              {initial.length >= 3 && <FitToPolygon points={initial} />}

              {/* Marcador del centro (referencia) */}
              {zone.lat && zone.lng && (
                <Marker
                  position={[zone.lat, zone.lng]}
                  interactive={false}
                  icon={L.divIcon({
                    className: "",
                    html: `<div style="background:#3949AB;color:white;padding:4px 8px;border-radius:6px;font-size:11px;font-weight:600;box-shadow:0 2px 6px rgba(0,0,0,0.3);white-space:nowrap;">📍 ${zone.name}</div>`,
                    iconSize: [0, 0],
                    iconAnchor: [0, 0],
                  })}
                />
              )}

              {/* Vértices arrastrables numerados */}
              {points.map((p, i) => (
                <Marker
                  key={`v-${i}`}
                  position={p}
                  icon={_vertexIcon(i, i === 0)}
                  draggable
                  eventHandlers={{
                    dragend: (e) => handleDragVertex(i, e.target.getLatLng()),
                    dblclick: () => handleDeleteVertex(i),
                  }}
                />
              ))}

              {/* Midpoints (solo en modo edición) — insertan nuevo vértice al hacer click */}
              {midpoints.map((m) => (
                <Marker
                  key={`mid-${m.edgeIdx}`}
                  position={m.pos}
                  icon={_midpointIcon}
                  eventHandlers={{ click: () => handleInsertMidpoint(m.edgeIdx) }}
                />
              ))}

              {/* Polígono */}
              {points.length >= 3 && (
                <Polygon
                  positions={points}
                  pathOptions={{
                    color: "#3949AB",
                    fillColor: "#3949AB",
                    fillOpacity: 0.2,
                    weight: 2,
                  }}
                />
              )}

              {/* Línea guía para polígonos incompletos (<3 puntos) */}
              {points.length === 2 && (
                <Polygon
                  positions={points}
                  pathOptions={{ color: "#3949AB", weight: 2, dashArray: "5,5", fillOpacity: 0 }}
                />
              )}
            </MapContainer>

            {/* Overlay de instrucciones si polígono vacío */}
            {points.length === 0 && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium text-neutral-700 pointer-events-none">
                <MapPin className="w-4 h-4 inline mr-2 text-[#3949AB]" strokeWidth={2.5} />
                Empieza haciendo clic en el mapa para trazar el área
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t px-5 py-3 flex items-center gap-3 shrink-0 bg-white">
            {err && (
              <div className="chip chip-danger" data-testid="zone-polygon-error">
                {err}
              </div>
            )}
            {saved && !err && (
              <div className="chip chip-success flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} /> Guardado
              </div>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button type="button" onClick={onClose} className="btn-secondary" data-testid="zone-polygon-cancel">
                Cancelar
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || points.length < 3}
                className="btn-primary"
                data-testid="zone-polygon-save"
              >
                <Save className="w-3.5 h-3.5" strokeWidth={2.5} />
                {saving ? "Guardando..." : "Guardar polígono"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
